import { BuyOrderModelType } from '@teinor/erp/types/company/order/buy/buyOrder'
import { connect, ConnectedProps } from 'react-redux'
import { DisplayPriceGettingParams } from '../../../baseComponents/Displays/DisplayPrice'
import { TwinTableLink } from '../../../baseComponents/TwinTable'
import { TableComponentProps } from '../../../baseComponents/TwinTable/BaseScreenTable/types'
import { CustomRenderString, CustomRenderDate, EditRow } from '../../../baseComponents/TwinTable/VirtualTableListing/CustomRenders'
import { ColumnTableSchema } from '../../../baseComponents/TwinTable/VirtualTableListing/Subcomponents/types'
import { OnRowClick } from '../../../baseComponents/TwinTable/VirtualTableListing/types'
import { TwinDictionary } from '../../../utils/globals/dictionary'
import locations from '../../../utils/globals/locations'
import withLoading from '../../../utils/hoc/withLoading'
import useTwinTranslation from '../../../utils/hooks/useTwinTranslation'
import { AllReduxPayloads } from '../../../utils/reducers'
import { BuyTagsPayload } from '../../../utils/reducers/buys/buyTags'
import { RenderBuyStatus, RenderBuyTags } from '../CustomRenderTable'

type TableBuyOrdersProps = TableComponentProps & ReduxTableBuyOrders & {
    onRowClick?: OnRowClick
}

const TableBuyOrders: React.FC<TableBuyOrdersProps> = ({ setTableInstance, onRowClick, buyTags}) => {
    const { t } = useTwinTranslation()
    const buyOrderStatusComplex: TwinDictionary = {
        0: { name: t('notProcessed', 'Sin procesar'), id: 0 },
        1: { name: t('waiting', 'Pendiente aprovación'), id: 1 },
        2: { name: t('processed', 'Procesado'), id: 2 },
    }
    const columns: ColumnTableSchema<BuyOrderModelType> = [
        {
            id: 'full_assigned_number',
            dataKey: 'full_assigned_number',
            label: t('nAssigned', 'Nº Asignado'),
            sortable: true,
            customRender: (parameterValue, allRowData) => <CustomRenderString value={parameterValue} className={allRowData?.assigned_number ? '' : 'text-red-BA'} />,
            searchableComponent: {
                component: 'SearchableComponentBasic',
                extraComponentData: { label: t('nAssigned', 'Nº Asignado') }
            },
            minWidth: 160,
        },
        {
            id: 'CustomerId',
            dataKey: 'CustomerId',
            label: t('customer', 'Cliente'),
            searchKey: 'Customer.name',
            minWidth: 130,
            customRender: (_parameterValue, allRowData) => <CustomRenderString value={allRowData?.Customer?.name || ''} />,
            searchableComponent: {
                component: 'SearchableComponentBasic',
                extraComponentData: { label: t('customer', 'Cliente') }
            },
            hideColumnByDefault: true
        },
        {
            id: 'SupplierId',
            dataKey: 'SupplierId',
            label: t('supplier', 'Proveedor'),
            searchKey: 'Supplier.name',
            minWidth: 130,
            customRender: (_parameterValue, allRowData) => <CustomRenderString value={allRowData?.Supplier?.name || ''} />,
            searchableComponent: {
                component: 'SearchableComponentBasic',
                extraComponentData: { label: t('supplier', 'Proveedor') }
            },
        },
        {
            id: 'supplier_number_order',
            dataKey: 'supplier_number_order',
            label: t('supplierNumberOrderS', 'Nº Prov.'),
            simpleLabel: t('supplierNumberOrder', 'Número pedido proveedor'),
            toolTip: t('supplierNumberOrder', 'Número pedido proveedor'),
            hideColumnByDefault: true,
            customRender: (parameterValue) => <CustomRenderString value={parameterValue} />,
            searchableComponent: {
                component: 'SearchableComponentBasic',
                extraComponentData: { label: t('supplierNumberOrder', 'Número pedido proveedor') }
            },
            minWidth: 120,
        },
        {
            id: 'status',
            dataKey: 'status',
            label: t('status', 'Estado'),
            sortable: true,
            minWidth: 130,
            customRender: (parameterValue) => <RenderBuyStatus value={parameterValue} />,
            searchableComponent: {
                component: 'SearchableComponentsCustomSelect',
                extraComponentData: { items: buyOrderStatusComplex, label: t('statusBuy', 'Estado de la compra') }
            },
        },
        {
            id: 'BillingWorkingCenterId',
            dataKey: 'BillingWorkingCenterId',
            searchKey: 'BillingWorkingCenter.name',
            label: t('requestedBy', 'Pedido por'),
            customRender: (_parameterValue, allRowData) => <CustomRenderString value={allRowData?.BillingWorkingCenter?.name || ''} />,
            searchableComponent: {
                component: 'SearchableComponentBasic',
                extraComponentData: { label: t('workingCenter', 'Centro de trabajo') }
            },
            minWidth: 120
        },
        {
            id: 'processed_date',
            dataKey: 'processed_date',
            label: t('createdDate', 'Fecha de creación'),
            customRender: (parameterValue, allRowData) => <CustomRenderDate value={parameterValue || allRowData.createdAt} className={parameterValue ? '' : 'text-red-BA'} />,
            sortable: true,
            minWidth: 165
        },
        {
            id: 'custom_name',
            dataKey: 'custom_name',
            label: t('customNameShort', 'Nombre pers.'),
            sortable: true,
            minWidth: 130,
            searchableComponent: {
                component: 'SearchableComponentBasic',
                extraComponentData: { label: t('customName', 'Nombre personalizado'), }
            },
            hideColumnByDefault: true
        },
        {
            id: 'total_without_vat',
            dataKey: 'total_without_vat',
            label: t('totalWithoutVatS', 'B. imponible'),
            simpleLabel: t('totalWithoutVat', 'Base imponible'),
            sortable: true,
            minWidth: 130,
            customRender: (parameterValue, allRowData) => <DisplayPriceGettingParams value={parseFloat(parameterValue)} with_vat={false} CurrencyId={allRowData.CurrencyId} />,
        },
        {
            id: 'total_total',
            dataKey: 'total_total',
            label: t('total', 'Total'),
            sortable: true,
            minWidth: 100,
            customRender: (parameterValue, allRowData) => <DisplayPriceGettingParams value={parseFloat(parameterValue)} with_vat={allRowData.with_vat} CurrencyId={allRowData.CurrencyId} />,
        },
        {
            id: 'CustomerProjectId',
            dataKey: 'CustomerProjectId',
            label: t('project', 'Proyecto'),
            hideColumnByDefault: true,
            customRender: (_parameterValue, allRowData) => <CustomRenderString value={allRowData?.CustomerProject?.name || ''} />
        },
        {
            id: 'CreateEmployeeId',
            dataKey: 'CreateEmployeeId',
            label: t('employee', 'Empleado'),
            searchKey: 'CreateEmployee.fullname_short',
            customRender: (_parameterValue, allRowData) => {
                const employeeName = allRowData?.CreateEmployee ? allRowData?.CreateEmployee?.fullname_short : allRowData?.ProcessEmployee?.fullname_short
                return <CustomRenderString value={employeeName || ''} />
            },
            searchableComponent: {
                component: 'SearchableComponentBasic',
                extraComponentData: { label: t('employee', 'Empleado') }
            },
        },
        {
            id: 'RepresentativeEmployeeId',
            dataKey: 'RepresentativeEmployeeId',
            label: t('representative', 'Representante'),
            searchKey: 'RepresentativeEmployee.fullname_short',
            customRender: (_parameterValue, allRowData) => {
                const employeeName = allRowData?.RepresentativeEmployee?.fullname_short
                return <CustomRenderString value={employeeName || ''} />
            },
            searchableComponent: {
                component: 'SearchableComponentBasic',
                extraComponentData: { label: t('representative', 'Representante') }
            },
            minWidth: 130
        },
        {
            id: 'CustomerProjectType',
            dataKey: 'id',
            hideColumnByDefault: true,
            label: t('projectTypeS', 'T. proyecto'),
            simpleLabel: t('projectType', 'Tipo de proyecto'),
            searchKey: 'CustomerProject.CustomerProjectType.name',
            searchableComponent: {
                component: 'SearchableComponentBasic',
                extraComponentData: { label: t('projectType', 'Tipo de proyecto') }
            },
            minWidth: 120,
            customRender: (parameterValue, allRowData) => <CustomRenderString value={allRowData?.CustomerProject?.CustomerProjectType?.name} />
        },
        {
            id: 'OrderTags',
            dataKey: 'OrderTags',
            label: t('tags', 'Etiquetas'),
            customRender: (_parameterValue, allRowData) => <RenderBuyTags {...{ allRowData, buyTags }} />,
            searchableComponent: {
                component: 'SearchableComponentsCustomSelectSearchBar',
                extraComponentData: { items: buyTags || {}, label: t('tags', 'Etiquetas') }
            },
            generalSearchable: false,
            sortable: false,
            hideColumnByDefault: true
        },
        {
            id: 'edit',
            dataKey: 'id',
            label: '',
            width: 50,
            simpleLabel: t('edit', 'Editar'),
            customRender: (parameterValue, allRowData) => <EditRow key={'edit' + parameterValue} {...{ parameterValue, allRowData }} />
        }
    ]
    return (
        <TwinTableLink createRowDataLink={[locations.buyOrderSingle, ['$id']]} name='buyOrders' columns={columns} getDataFrom='/api/app/order/buy/buyOrder/getAllBuyOrdersComplete' onRowClick={onRowClick} setTableInstance={setTableInstance} />
    )
}

const TableBuyOrdersDispatch = {
    setBuyTags: (payload: BuyTagsPayload) => ({ type: 'CHANGE_BUY_TAGS', payload }),
}

export type ReduxTableBuyOrders = ConnectedProps<typeof tableBuyOrdersConnect>
const mapTableBuyOrdersConnector = (state: AllReduxPayloads) => ({buyTags: state.buyTags })
const tableBuyOrdersConnect = connect(mapTableBuyOrdersConnector, TableBuyOrdersDispatch)
const TableBuyOrdersConnectLoading = withLoading(TableBuyOrders, [{ fetchUrl: '/api/app/order/buy/tag/getAllBuyTags', propName: 'buyTags', setFunctionName: 'setBuyTags' }])
const TableBuyOrdersConnect = tableBuyOrdersConnect(TableBuyOrdersConnectLoading)

export default TableBuyOrdersConnect